/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import Cleaner from '../cleaner/cleaner';

// TODO #84 Přerušení stahování.
// - Downloader#cancel()
// - Zruší probíhající stahování.
// - Zde by se mělo pokusit přerušit aktuální stahování. Každopádně přerušit sérii.
// - Neměl by být vstupem downloadPromise, pokud by bylo v budoucnu více paralelních stahování?
// - Možná inspirace rozhraním $timeout: https://docs.angularjs.org/api/ng/service/$timeout
// TODO Přejmenovat? AssetReplacer, ContentDownloader
/**
 * Stahovač obsahu
 *
 * ![Diagram tříd](./core/downloader/downloader-class.doc.png)
 *
 * ![Diagram aktivit](./core/downloader/downloader-activity.doc.png)
 * @implements {EventBus}
 */
class Downloader {
  /**
   * @param {AssetDownloader} assetDownloader
   * @param {Object} config Konfigurace aplikace.
   * @param {Detector} detector
   * @param {Logger} logger
   * @param {Store} primaryStore Hlavní datový sklad ve kterém jsou uloženy soubory a přehrávaný obsah.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   */
  constructor(
    assetDownloader,
    config,
    detector,
    logger,
    primaryStore,
    session,
    viewerRegistry
  ) {
    this.assetDownloader = assetDownloader;
    const cleaner = new Cleaner(assetDownloader, primaryStore, session);
    this.cleaner = cleaner;
    this.defaultSize = config.device.downloader.defaultSize;
    this.detector = detector;
    this.logger = logger;
    this.viewerRegistry = viewerRegistry;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.primaryStore = primaryStore;
    // Navázání naslouchání na instanci.
    this.onContent = this.onContent.bind(this);
    this.onError = this.onError.bind(this);
    // Naslouchání událostem.
    cleaner.on('content', this.onContent);
    cleaner.on('error', this.onError);
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * @returns {undefined} Nic.
   */
  destroy() {
    // TODO #84 Přerušení stahování.
    // - Půjde synchronně? Asi ne.
    // - Minimálně volat cancel.
    const { cleaner } = this;
    cleaner.off('content', this.onContent);
    cleaner.off('error', this.onError);
  }

  /**
   * Stáhne soubory z nového obsahu.
   * Při paralelním stahování více obsahů, může dojít k problémům
   * v synchronizaci, např. paralelní stahování stejného souboru
   * do stejného umístění.
   * Proto se doporučuje tuto metudo vždy volat sériově.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<Array.<Asset>>} Seznam stažených assetů.
   */
  download(contentInfo) {
    // TODO #84 Zvážit, jak signalizovat přerušení stahování:
    // - Místo assetIdArray vracet null.
    //   - Ignorovat rozestahované soubory?
    // - Vracet assetIdArray a příznak přerušení.
    //   - Rodič rozhodne co se soubory při přerušení.
    // TODO Šlo by přepsat na jeden reduce (místo map, filter a reduce).
    return new Promise((resolve) => {
      this.logger.debug('Downloader#download');
      return resolve(
        // Výběr médií ke stažení.
        // TODO #84 Stahovat pouze užitečné soubory:
        // - Ideálně pouze z hlavního tasku a z jeho médií.
        // - Možnost použít ScheduleManager#findTask().
        // - Zohlednit jak se bude stahovat obsah s více plánovanými úkoly.
        contentInfo.mediumArray
          .map((medium) => ({
            medium,
            remoteUrl: medium.url,
            viewerType: this.viewerRegistry.find(medium.type)
          }))
          .filter(
            ({ remoteUrl, viewerType }) =>
              // Musí být definované URL média na serveru, ze kterého se stáhne.
              !isNil(remoteUrl) &&
              // Médium musí mít typ, který vyžaduje stažení.
              viewerType &&
              viewerType.isOfflineable()
          )
          // Sériové stahování souborů pro jednotlivá média.
          .reduce(
            (accumulator, { medium, remoteUrl, viewerType }) =>
              accumulator.then((assetArray) =>
                this.downloadAsset(contentInfo, medium, remoteUrl, viewerType)
                  // Navázání média na vytvořený soubor.
                  .then((asset) => {
                    // Příprava napojení média v novém obsahu na asset, který byl stažen.
                    // Napojení se aktivuje až v okamžiku vložení nového obsahu do hlavního datového skladu.
                    contentInfo.store.inject('medium', {
                      assetId: asset.id,
                      id: medium.id
                    });
                    // Úspěšně stažený asset se přidá do výstupu.
                    assetArray.push(asset);
                  })
                  // TODO #84 Zde by se mělo kontrolovat, jestli nebylo vyžádáno přerušení.
                  // Pokud něco selže, tak se přeskočí na další médium.
                  .catch((error) => {
                    this.logger.error(error);
                  })
                  // Předání akumulovaných výsledků pro další médium.
                  // TODO Vrací se objekty, které jsou v primaryStore a ne v contentStore
                  .then(() => assetArray)
              ),
            Promise.resolve([])
          )
      );
    });
  }

  /**
   * Stáhne jeden soubor z nového obsahu.
   * @param {ContentInfo} contentInfo
   * @param {Medium} medium
   * @param {string} remoteUrl
   * @param {ViewerType} viewerType
   * @returns {Promise.<Asset>}
   */
  downloadAsset(contentInfo, medium, remoteUrl, viewerType) {
    return new Promise((resolve) => {
      const { defaultSize } = this;
      this.logger.debug(`Downloader#downloadAsset ${remoteUrl}`);
      return resolve(
        // Detekce dostatečného místa k uložení souboru.
        // Zatím se používá pouze výchozí hodnota z konfigurace.
        // Nezjišťuje se skutečná velikost souboru.
        this.detector
          .detect(defaultSize)
          .then((detected) => {
            // Pokud je dostatek místa, není třeba místo uvolňovat.
            if (detected) {
              return;
            }
            // Pokud není dostatek místa, provede se pokus o jeho uvolnění.
            return this.cleaner
              .clean(contentInfo)
              .then(() => this.detector.detect(defaultSize))
              .then((detectedAfterClean) => {
                // Uvolnil se dostatek místa, soubor lze stáhnout.
                if (detectedAfterClean) {
                  return;
                }
                // Pokud ani po uvolnění není dostatek místa, vyhodí se chyba,
                // která se zaloguje.
                throw Error(`Unable to clean space for medium ${medium}.`);
              });
          })
          // Selhání v procesu čištění místa nezastaví pokus o stažení souboru.
          // Jinak by hrozilo, že se zablokuje stahování, i když by to nebylo nutné.
          // Stejně tak se provádí pokus o stažení souboru většího než defaultSize.
          .catch((error) => {
            this.logger.error(error);
          })
          // Persistentní uložení souboru do hlavního datového skladu.
          .then(() =>
            this.assetDownloader.download(remoteUrl, {
              cache: !viewerType.getCheckFile(medium),
              checksum: medium.checksum,
              extract: viewerType.isExtractable()
            })
          )
      );
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na požadavek na přepnutí obsahu z čističe obsahu.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @private
   * @returns {undefined} Nic.
   */
  onContent(contentInfo) {
    this.emitter.emit('content', contentInfo);
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.logger.error(error);
  }

  /**
   * Obnoví obsah datového zdroje s binárními soubory.
   * @returns {Promise.<Array.<Asset>>} Seznam obnovených assetů.
   */
  start() {
    return this.assetDownloader.restore().catch((error) => {
      this.logger.error(error);
    });
  }
}

/**
 * Nový obsah zařízení k zobrazení.
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @event Downloader#content
 * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
 */

export default Downloader;
